/**
 * OZK 콘텐츠 제작 및 저작권 정책 (Editorial & Copyright Policy)
 */

import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { Separator } from "@/components/ui/separator";

export default function EditorialPolicy() {
  return (
    <>
      <Seo
        title="OZK 콘텐츠 제작 및 저작권 정책 — OZK News"
        description="OZK News의 콘텐츠 제작 원칙, AI 활용 정책, 저작권 정책 및 정정 절차"
        path="/editorial-policy"
      />

      <PageContainer size="read">
        <Link href="/">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold mb-2">
              📘 OZK 콘텐츠 제작 및 저작권 정책
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Editorial & Copyright Policy
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              최종 수정: 2025년 1월 | 적용 대상: ozknews.com 및 OZK NEWS 관련 서비스 일체
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                1️⃣ 콘텐츠 제작 원칙 (Editorial Principles)
              </h2>
              <p className="text-muted-foreground mb-4">
                OZK NEWS는 공정하고 정확한 정보 제공을 목표로 하며, 기사와 콘텐츠는 다음 기준에 따라 제작됩니다.
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  모든 기사 및 콘텐츠는 OZK NEWS 기자와 편집진이 작성하거나, AI Assistant의 보조를 받아 공개된 자료와 사실을 기반으로 작성됩니다.
                </li>
                <li>
                  AI는 기사 작성의 <strong>도우미(AI-assisted tool)</strong>로만 사용되며, 기사 내용의 최종 검토·책임은 인간 기자와 편집자에게 있습니다.
                </li>
                <li>
                  사실 확인(fact-checking)과 신뢰할 수 있는 출처를 기반으로 작성하며, 명예훼손·허위정보·차별적 표현을 방지하기 위해 지속적으로 검수합니다.
                </li>
              </ul>
            </section>

            <Separator />

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                2️⃣ AI 활용 정책 (AI-Assisted Content Policy)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>AI는 기사 초안 작성, 번역, 요약, 데이터 분석 등에 사용될 수 있습니다.</li>
                <li>AI 생성 콘텐츠는 기자·편집자의 검토와 수정을 거친 후 게시됩니다.</li>
                <li>AI가 제공한 내용 중 오류가 확인될 경우, 즉시 검토 및 수정 조치를 취합니다.</li>
                <li>모든 AI 관련 기사는 기사 하단에 AI 활용에 대한 안내 문구를 포함합니다.</li>
              </ul>
            </section>

            <Separator />

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                3️⃣ 저작권 정책 (Copyright & Image Use Policy)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  OZK NEWS가 작성한 모든 콘텐츠의 저작권은 OZK NEWS 또는 원저작자에게 귀속됩니다.
                </li>
                <li>
                  외부 기사, 보고서, 또는 공공 데이터의 내용을 인용할 경우 저작권법에 따른 공정 이용(Fair Dealing) 범위 내에서 요약·재구성하여 사용합니다.
                </li>
                <li>
                  이미지, 영상, 그래픽 등은 ① 직접 촬영, ② 공개 라이선스(CC0·Unsplash 등), ③ 합법적 AI 생성 이미지, 또는 ④ 정당한 사용 허가를 받은 자료만 활용합니다.
                </li>
                <li>
                  저작권 침해 의심 자료가 발견될 경우, 즉시 삭제·정정 또는 대체 이미지로 교체합니다.
                </li>
              </ul>
            </section>

            <Separator />

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                4️⃣ 사실 오류 및 정정 절차 (Corrections & Updates)
              </h2>
              <p className="text-muted-foreground mb-4">
                OZK NEWS는 정확성을 최우선으로 합니다. 그러나 오류나 오해의 소지가 있는 내용이 발견되면, 다음 절차로 신속히 조치합니다.
              </p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>독자 또는 관계자 신고 접수 (ozknews.com@gmail.com)</li>
                <li>편집팀의 사실 검증 및 책임자 검토</li>
                <li>수정 또는 삭제 후 기사 하단에 정정일시 표기</li>
                <li>필요 시 관련자에게 직접 통보</li>
              </ol>
              <p className="text-muted-foreground mt-4">
                정정 요청은 누구나 제출할 수 있으며, 확인된 오류는 가능한 한 빠르게 수정됩니다.
              </p>
            </section>

            <Separator />

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                5️⃣ 독자 참여 및 신고 (Reader Feedback & Complaints)
              </h2>
              <p className="text-muted-foreground mb-4">
                OZK NEWS는 독자의 의견과 제보를 환영합니다. 기사 내용 중 허위사실, 명예훼손, 저작권 침해, AI 오작성 등 문제가 있는 경우
              </p>
              <p className="text-muted-foreground mb-2">
                📧 <a href="mailto:ozknews.com@gmail.com" className="text-primary hover:underline">ozknews.com@gmail.com</a> 또는 사이트 내 신고 기능을 통해 알려주십시오.
              </p>
              <p className="text-muted-foreground">
                검토 후 사실로 확인될 경우 즉시 조치하며, 필요 시 해당 콘텐츠를 삭제 또는 정정합니다.
              </p>
            </section>

            <Separator />

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                6️⃣ 책임 한계 및 면책 (Disclaimer)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  OZK NEWS는 공개 자료 및 사실 기반으로 콘텐츠를 제작하지만, 완전한 정확성 또는 시의성을 항상 보장하지는 않습니다.
                </li>
                <li>
                  본 사이트의 정보는 참고용이며, 투자·의료·법률 등 전문적 판단이 필요한 사안에 대한 공식 자문으로 간주되지 않습니다.
                </li>
                <li>
                  사용자 게시글·댓글·외부 링크의 내용은 개별 작성자에게 책임이 있으며, OZK NEWS는 이에 대해 법적 책임을 지지 않습니다.
                </li>
              </ul>
            </section>

            <Separator />

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                7️⃣ 정책 변경 (Policy Updates)
              </h2>
              <p className="text-muted-foreground">
                본 정책은 법적 기준, 기술적 환경, 내부 절차에 따라 변경될 수 있습니다. 중대한 변경이 있을 경우, 홈페이지 및 공지사항을 통해 사전 고지합니다.
              </p>
            </section>

            <Separator />

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold mb-4">
                8️⃣ 문의처 (Contact Information)
              </h2>
              <div className="space-y-2 text-muted-foreground">
                <p className="font-semibold text-foreground">OZK NEWS 편집팀 (Editorial Office)</p>
                <p>📧 <a href="mailto:ozknews.com@gmail.com" className="text-primary hover:underline">ozknews.com@gmail.com</a></p>
                <p>📍 160 Midson Road, Epping NSW 2121, Australia</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </PageContainer>
    </>
  );
}

